import { type ProgramSlug } from './schemas';

export interface Week {
  n: number;
  title: string;
  focus: string;
  description: string;
}

export interface AddOn {
  name: string;
  bullets: string[];
}

export interface Pricing {
  foundingRate: number;        // $597
  standardRate: number;        // $750
  foundingPayments: [number, number]; // [325, 325]
  standardPayments: [number, number]; // [400, 400]
  postProgramSession: number;  // $150
  monthlyMaintenance: string;  // "$275-300/month (2 sessions)"
  parentSession: number;       // $175
}

export interface Program {
  slug: ProgramSlug;
  name: string;

  // Card view
  tagline: string;
  shortDescription: string;
  bestFor: string;
  accent: 'sage' | 'tan' | 'cream';

  // Detail page hero
  hero: {
    eyebrow: string;
    headline: string;
    subhead: string;
  };

  problem?: {
    title: string;
    intro: string;
    bullets: string[];
    closer?: string;
  };

  solution?: {
    title: string;
    intro: string;
    bullets: string[];
  };

  whoFor: string[];

  howItWorks: string[];

  whatLearn?: string[];   // Slide 9 only (Own Your Lane)

  outcomes: string[];

  weeks: Week[];

  addOns?: AddOn[];        // Own Your Future only

  scopeProminent?: boolean; // Light Your Lane = true
}

export const pricing: Pricing = {
  foundingRate: 597,
  standardRate: 750,
  foundingPayments: [325, 325],
  standardPayments: [400, 400],
  postProgramSession: 150,
  monthlyMaintenance: '$250/month (2 sessions)',
  parentSession: 175,
};

export const programs: Program[] = [
  /* =====================================================================
   * Own Your Lane, Slides 5, 6, 7, 8, 9
   * ===================================================================== */
  {
    slug: 'own-your-lane',
    name: 'Own Your Lane',
    tagline: 'Academic success and emotional resilience',
    shortDescription:
      'A 6-week 1:1 coaching program that helps middle, high school, and college students step out of feeling overwhelmed and build the habits, confidence, and self-awareness to take ownership of their path.',
    bestFor:
      'Students feeling overwhelmed, unmotivated, or unsure of themselves, and ready to grow.',
    accent: 'sage',

    hero: {
      eyebrow: '6-Week 1:1 Coaching Program',
      headline: 'Confidence. Clarity. Resilience.',
      subhead:
        'Helping students step out of feeling overwhelmed and into ownership of their life, learning, and future.',
    },

    problem: {
      title: 'The Problem (what parents & students feel)',
      intro: 'Does your student struggle with:',
      bullets: [
        'Feeling overwhelmed by school or responsibilities',
        'Lack of motivation or confidence',
        'Poor time management or organization',
        'Test anxiety or stress',
        'Difficulty navigating friendships or communication',
      ],
      closer: 'You’re not alone, and your student isn’t stuck.',
    },

    solution: {
      title: 'The Solution',
      intro:
        'Own Your Lane is a personalized 6-week coaching program designed to help students:',
      bullets: [
        'Build strong academic habits',
        'Develop emotional resilience',
        'Strengthen confidence and self-awareness',
        'Learn practical tools for managing stress and responsibilities',
        'Take ownership of their path forward',
      ],
    },

    whoFor: [
      'In middle school, high school, or college',
      'Feeling overwhelmed, unmotivated, or unsure of themselves',
      'Needing structure, guidance, and encouragement',
      'Ready to grow, learn, and build confidence',
    ],

    howItWorks: [
      '6 weekly 1:1 coaching sessions (50-60 minutes)',
      'Personalized strategies tailored to each student',
      'Optional parent check-in',
      'Supportive, encouraging, and structured environment',
    ],

    whatLearn: [
      'Discover their strengths and build confidence',
      'Learn time management and organization systems',
      'Develop healthy coping strategies for stress and anxiety',
      'Improve communication and self-advocacy skills',
      'Build resilience and a growth mindset',
      'Create a personalized plan for continued success',
    ],

    outcomes: [
      'Feel more confident in themselves',
      'Have clear systems for school and responsibilities',
      'Be better equipped to handle stress and challenges',
      'Take ownership of their decisions and future',
    ],

    weeks: [
      { n: 1, title: 'Discover Your Strengths', focus: 'Self-Awareness',
        description: 'Build self-awareness and set meaningful goals.' },
      { n: 2, title: 'Get Organized & Focused', focus: 'Systems',
        description: 'Learn time management and study strategies.' },
      { n: 3, title: 'Manage Stress & Emotions', focus: 'Coping',
        description: 'Develop coping tools and emotional awareness.' },
      { n: 4, title: 'Build Confidence & Communication', focus: 'Voice',
        description: 'Strengthen social skills and self-advocacy.' },
      { n: 5, title: 'Take Ownership', focus: 'Growth Mindset',
        description: 'Develop accountability and a growth mindset.' },
      { n: 6, title: 'Plan Your Path Forward', focus: 'Sustainability',
        description: 'Create a sustainable success plan.' },
    ],
  },

  /* =====================================================================
   * Own Your Future, Slides 10, 11, 12, 13, 14, 15
   * ===================================================================== */
  {
    slug: 'own-your-future',
    name: 'Own Your Future',
    tagline: 'Academic resilience plus college and career coaching',
    shortDescription:
      'A 6-week program for high school juniors, seniors, and college students who want to build academic discipline, clarify their direction, and create an actionable plan for what comes next.',
    bestFor:
      'High school juniors and seniors, and college students who feel lost or stuck.',
    accent: 'tan',

    hero: {
      eyebrow: '6-Week Academic Resilience + College/Career Coaching',
      headline: 'Build the discipline. Clarify the direction. Make the plan.',
      subhead:
        'Helping high school and college students build academic resilience, clarify their future direction, and create actionable plans with confidence.',
    },

    whoFor: [
      'High school juniors and seniors',
      'College students feeling lost or overwhelmed',
      'Students lacking motivation or direction',
      'Students preparing for college, careers, or major transitions',
    ],

    howItWorks: [
      '6 weekly 1:1 coaching sessions (50-60 minutes)',
      'Personalized strategies tailored to each student’s direction and stage',
      'Optional parent check-in',
      'A structured, supportive environment for real planning',
    ],

    outcomes: [
      'Have a clear sense of direction',
      'Build stronger academic habits',
      'Feel more confident about their future',
      'Know how to manage stress and pressure',
      'Have a structured plan to move forward',
      'Develop independence and accountability',
    ],

    weeks: [
      { n: 1, title: 'Where Are You Now?', focus: 'Awareness + Direction',
        description: 'Assess current academic habits and mindset, identify stress points and roadblocks, explore interests, strengths, and values, and begin the future-vision conversation.' },
      { n: 2, title: 'Academic Resilience & Discipline', focus: 'Consistency Over Motivation',
        description: 'Understand why motivation comes and goes, build discipline systems, and strengthen study habits and routines.' },
      { n: 3, title: 'Managing Stress, Pressure & Burnout', focus: 'Emotional Resilience',
        description: 'Identify stress triggers (tests, deadlines, expectations), develop coping strategies, and prevent burnout.' },
      { n: 4, title: 'College & Career Exploration', focus: 'Direction + Possibility',
        description: 'Explore career interests and pathways, discuss college vs alternative paths, and align strengths with opportunities.' },
      { n: 5, title: 'Goal Setting & Action Planning', focus: 'Clarity + Execution',
        description: 'Set short-term (30-90 day) goals, set long-term (1-5 year) goals, and break goals into actionable steps.' },
      { n: 6, title: 'Build Your Future Plan', focus: 'Independence + Sustainability',
        description: 'Create a personalized success roadmap, identify obstacles and solutions, and build confidence in next steps. Final deliverable: "My Future Plan."' },
    ],

    addOns: [
      { name: 'Add-On Package, College & Career Coaching',
        bullets: [
          'Application timeline planning',
          'College list support',
          'Essay brainstorming and editing',
          'FAFSA guidance sessions',
          'Resume support',
          'Career exploration',
        ] },
      { name: 'VIP Intensive, “College Readiness Intensive”',
        bullets: [
          '2-3 focused sessions',
          'Application setup',
          'Timeline organization',
          'Essay planning',
        ] },
      { name: 'Monthly Coaching, “Future Planning Coaching”',
        bullets: [
          'Accountability',
          'Deadlines',
          'Applications',
          'Scholarships',
        ] },
    ],
  },

  /* =====================================================================
   * Light Your Lane, Slides 16, 17, 18, 19, 20
   * ===================================================================== */
  {
    slug: 'light-your-lane',
    name: 'Strength Within',
    tagline: 'Emotional resilience and confidence',
    shortDescription:
      'A 6-week program focused on managing anxiety, building confidence from within (not comparison), and developing healthy coping strategies for school stress, friendships, and social pressure.',
    bestFor:
      'Students struggling with anxiety, self-worth, peer pressure, or social media comparison.',
    accent: 'cream',
    scopeProminent: true,

    hero: {
      eyebrow: '6-Week Emotional Resilience & Confidence Coaching',
      headline: 'Calm. Confidence. Steady ground.',
      subhead:
        'Building confidence, managing anxiety and social pressures, and developing healthy coping strategies for everyday challenges.',
    },

    whoFor: [
      'Students experiencing anxiety or stress related to school',
      'Students struggling with confidence or self-worth',
      'Students dealing with peer issues or bullying',
      'Students impacted by social media comparison',
      'Students who feel overwhelmed emotionally',
    ],

    howItWorks: [
      '6 weekly 1:1 coaching sessions (50-60 minutes)',
      'Skill-based, age-appropriate strategies for emotional regulation',
      'Optional parent check-in',
      'A calm, encouraging environment: never clinical, never diagnostic',
    ],

    outcomes: [
      'Feel more confident in who they are',
      'Have tools to manage anxiety and stress',
      'Understand how to navigate friendships',
      'Reduce comparison and social media pressure',
      'Build emotional awareness and control',
      'Feel more grounded, calm, and capable',
    ],

    weeks: [
      { n: 1, title: 'Understanding Your Emotions', focus: 'Awareness + Safety',
        description: 'Normalize emotions, identify common stressors (school, peers, expectations), and build emotional awareness. You can’t manage what you don’t understand.' },
      { n: 2, title: 'Managing Anxiety & School Stress', focus: 'Coping Tools',
        description: 'Breathing and reset techniques, thought reframing (what’s true vs what feels true), and breaking overwhelming tasks into steps.' },
      { n: 3, title: 'Confidence & Self-Worth', focus: 'Identity + Inner Voice',
        description: 'Explore self-talk patterns, build confidence from within (not comparison), and challenge negative beliefs.' },
      { n: 4, title: 'Social Media & Comparison', focus: 'Protecting Your Mind',
        description: 'Understand the impact of comparison, build awareness of social media triggers, and develop healthy boundaries.' },
      { n: 5, title: 'Friendships, Conflict & Boundaries', focus: 'Healthy Relationships',
        description: 'Identify healthy vs unhealthy friendships, learn communication skills, and build boundaries and self-respect.' },
      { n: 6, title: 'Resilience & Moving Forward', focus: 'Strength + Independence',
        description: 'Reflect on growth, build a personal resilience plan, and prepare for future challenges. Final activity: "My Strength Plan."' },
    ],
  },
];

export function getProgram(slug: string): Program | undefined {
  return programs.find((p) => p.slug === slug);
}
